import { useMotionValueEvent, useScroll } from 'framer-motion';
import { useRef, useState } from 'react';

import Motion from '@/app/components/Motion';

import PopHighlightedFeaturesSection from './PopHighlightedFeaturesSection';
import type { PopHighlightedFeaturesSectionData } from './types';

export interface PopHighlightedFeaturesProps {
  readonly sections: PopHighlightedFeaturesSectionData[];
}

const PopHighlightedFeatures = ({ sections }: PopHighlightedFeaturesProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });

  useMotionValueEvent(scrollYProgress, 'change', (latest): void => {
    const newActiveSectionIndex = Math.min(
      Math.floor(latest * sections.length),
      sections.length - 1,
    );
    if (activeSectionIndex !== newActiveSectionIndex) {
      setActiveSectionIndex(newActiveSectionIndex);
    }
  });

  return (
    <Motion
      className="lg:relative"
      data-cy="pop-highlighted-features"
      initial={{ opacity: 0 }}
      ref={ref}
      transition={{ duration: 0.75, type: 'tween' }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1 }}
    >
      <div className="lg:motion-safe:js:absolute lg:motion-safe:js:size-full">
        <div className="lg:motion-safe:js:sticky lg:motion-safe:js:top-0 lg:motion-safe:js:h-screen lg:motion-safe:js:w-full">
          {sections.map((section, i) => (
            <PopHighlightedFeaturesSection
              activeIndex={activeSectionIndex}
              index={i}
              key={section.id}
              {...section}
            />
          ))}
        </div>
      </div>
      <div
        className="hidden lg:motion-safe:js:block"
        data-cy="pop-highlighted-features-spacer"
        style={{ height: `${sections.length * 100}vh` }}
      />
    </Motion>
  );
};

export default PopHighlightedFeatures;
